/**
 * Page-level tutorial button + auto-open hook.
 *
 * Drop on any page:
 *   <PageTutorial id="dashboard" />
 *
 * Tour suppression keys (localStorage):
 *   - resmo:tutorial:<id>:done        per-tour (set when the user completes a walk)
 *   - resmo:tutorials:disable-all     global  (set when the user picks "Don't ask again")
 *
 * If either key is set, the auto-open behaviour is suppressed. The floating
 * "Tour" pill is still rendered so the user can opt back in any time.
 */
import React, { useEffect, useState } from "react";
import { HelpCircle } from "lucide-react";
import TutorialOverlay, { tutorialDone } from "./TutorialOverlay";
import { TUTORIALS } from "../lib/tutorialSteps";

const DISABLE_ALL_KEY = "resmo:tutorials:disable-all";

export function tutorialsGloballyDisabled() {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(DISABLE_ALL_KEY) === "1"; }
  catch { return false; }
}

export function setTutorialsGloballyDisabled(value) {
  if (typeof window === "undefined") return;
  try {
    if (value) window.localStorage.setItem(DISABLE_ALL_KEY, "1");
    else       window.localStorage.removeItem(DISABLE_ALL_KEY);
  } catch { /* ignore */ }
}

export default function PageTutorial({ id, autoOpenDelay = 1200, disableAutoOpen = false }) {
  const [open, setOpen] = useState(false);
  const [disableAll, setDisableAll] = useState(tutorialsGloballyDisabled());
  const steps = TUTORIALS[id];

  useEffect(() => {
    if (!steps || disableAutoOpen) return undefined;
    if (tutorialDone(id) || disableAll) return undefined;
    const t = setTimeout(() => setOpen(true), autoOpenDelay);
    return () => clearTimeout(t);
  }, [id, steps, autoOpenDelay, disableAutoOpen, disableAll]);

  if (!steps) return null;

  const handleClose = (info) => {
    setOpen(false);
    if (info && info.dontAskAgain) {
      setTutorialsGloballyDisabled(true);
      setDisableAll(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          // Re-opening manually also re-enables future auto-opens so the
          // user can come back if they later change their mind.
          if (disableAll) {
            setTutorialsGloballyDisabled(false);
            setDisableAll(false);
          }
          setOpen(true);
        }}
        className="fixed flex items-center gap-1.5 px-3 py-2 transition-transform hover:-translate-y-0.5"
        style={{
          bottom: 80,
          left: 16,
          background: "white",
          border: "3px solid #0F1B2D",
          borderRadius: 999,
          boxShadow: "4px 4px 0 0 #0F1B2D",
          zIndex: 40,
          fontSize: 11,
          fontWeight: 700,
          color: "#0F1B2D",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
        data-testid={`page-tutorial-btn-${id}`}
        title="Show me how this page works"
      >
        <HelpCircle size={13} style={{ color: "#FF6A00" }} />
        Tour
      </button>
      <TutorialOverlay
        id={id}
        open={open}
        steps={steps}
        onClose={handleClose}
      />
    </>
  );
}
